import { apiService } from './apiService';
import { notificationStorageService } from './notificationStorageService';

class AuthService {
  // Login user
  async login(email, password) {
    try {
      // Use skipTokenRefresh to prevent 401 from triggering logout event
      // Login failures are expected and shouldn't cause a logout redirect
      const response = await apiService.post('/login', {
        user: {
          email,
          password,
        },
      }, { skipTokenRefresh: true, includeAuth: false });

      // The JWT token is automatically handled by apiService
      // Return the user data from the response
      if (response.status && response.status.data && response.status.data.user) {
        const user = response.status.data.user;

        // Store user data in localStorage
        localStorage.setItem('current_user', JSON.stringify(user));

        // Dispatch login event
        window.dispatchEvent(new CustomEvent('auth:login', { detail: user }));

        return {
          success: true,
          user,
          message: response.status.message || 'Logged in successfully',
        };
      }

      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Login error:', error);

      // Extract error message from various API response formats
      let errorMessage = 'Login failed. Please try again.';

      if (error.data && typeof error.data === 'object') {
        // Rails/Devise: { error: "Invalid Email or password." }
        // Our API:      { status: { message: "..." } }
        // Simple:       { message: "..." }
        errorMessage =
          error.data.error ||
          error.data.message ||
          error.data.status?.message ||
          errorMessage;
      } else if (typeof error.data === 'string' && error.data.length > 0) {
        // Plain text response body
        errorMessage = error.data;
      } else if (
        error.message &&
        !error.message.startsWith('HTTP ') &&
        error.message !== 'Network error. Please check your connection and try again.'
      ) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
        error,
      };
    }
  }

  // Register user — now returns requires_verification: true (no JWT yet)
  async register(userData) {
    try {
      const response = await apiService.post('/signup', {
        user: userData,
      });

      // New flow: BE returns requires_verification flag instead of a JWT
      if (response.requires_verification) {
        return {
          success: true,
          requiresVerification: true,
          email: response.email,
          message: response.status?.message || 'Verification email sent.',
        };
      }

      // Fallback: old flow (should not happen in production)
      if (response.status && response.data) {
        const user = response.data;
        localStorage.setItem('current_user', JSON.stringify(user));
        window.dispatchEvent(new CustomEvent('auth:login', { detail: user }));
        return {
          success: true,
          user,
          message: response.status.message || 'Account created successfully',
        };
      }

      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = 'Registration failed. Please try again.';
      if (error.status === 422) {
        errorMessage = error.data?.status?.message || 'Registration failed. Please check your information.';
      }
      return {
        success: false,
        message: errorMessage,
        error,
      };
    }
  }

  // Verify OTP after registration
  async verifyOtp(email, otpCode) {
    try {
      const response = await apiService.post('/verify_otp', { email, otp_code: otpCode }, { includeAuth: false });

      if (response.success && response.data) {
        const user = response.data;
        // Token is returned in body AND Authorization header — apiService picks up the header automatically
        localStorage.setItem('current_user', JSON.stringify(user));
        window.dispatchEvent(new CustomEvent('auth:login', { detail: user }));
        return { success: true, user };
      }

      throw new Error('Invalid response format');
    } catch (error) {
      const message =
        error.data?.message ||
        error.message ||
        'Verification failed. Please try again.';
      return { success: false, message };
    }
  }

  // Resend OTP
  async resendOtp(email) {
    try {
      await apiService.post('/resend_otp', { email }, { includeAuth: false });
      return { success: true };
    } catch (error) {
      return { success: false };
    }
  }

  // Handle unverified user trying to login — redirect to OTP page
  isUnverifiedLoginError(error) {
    return error?.data?.requires_verification === true;
  }

  // Logout user
  async logout() {
    try {
      // Call logout endpoint with proper method
      try {
        await apiService.delete('/logout');
      } catch (error) {
        // Continue with local logout even if server logout fails
        console.warn('Server logout failed, continuing with local logout:', error);
      }

      // Clear local storage
      apiService.removeToken();
      localStorage.removeItem('current_user');

      // Clear locally stored notifications
      notificationStorageService.clearAll();

      // Dispatch logout event
      window.dispatchEvent(new CustomEvent('auth:logout'));

      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return {
        success: false,
        message: error.message || 'Logout failed',
        error,
      };
    }
  }

  // Get current user
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('current_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  // Get current auth token
  getToken() {
    return apiService.getStoredToken();
  }

  // Check if user is authenticated
  isAuthenticated() {
    return apiService.isAuthenticated() && !!this.getCurrentUser();
  }

  // Check user role
  hasRole(role) {
    const user = this.getCurrentUser();
    return user && user.role === role;
  }

  // Forgot password — Devise: POST /password { user: { email } }
  async forgotPassword(email) {
    try {
      await apiService.post('/password', {
        user: { email },
      }, { includeAuth: false });

      return {
        success: true,
        message: 'If that email exists, a reset link has been sent.',
      };
    } catch (error) {
      // Devise may return 200 even for unknown emails (security).
      // Only show generic message to avoid user enumeration.
      return {
        success: false,
        message: error.data?.errors?.[0] || error.data?.error || 'Failed to send password reset email. Please try again.',
      };
    }
  }

  // Reset password — Devise: PUT /password
  // { user: { reset_password_token, password, password_confirmation } }
  async resetPassword(token, password, passwordConfirmation) {
    try {
      await apiService.put('/password', {
        user: {
          reset_password_token: token,
          password,
          password_confirmation: passwordConfirmation,
        },
      }, { includeAuth: false });

      return {
        success: true,
        message: 'Password reset successfully. You can now sign in.',
      };
    } catch (error) {
      // Devise returns { errors: { ... } } on validation failure
      let errorMessage = 'Failed to reset password. Please try again.';
      if (error.data?.errors) {
        const errors = error.data.errors;
        if (typeof errors === 'object' && !Array.isArray(errors)) {
          // { errors: { reset_password_token: ["has expired"], password: ["is too short"] } }
          const messages = Object.entries(errors)
            .map(([field, msgs]) => {
              const label = field.replace(/_/g, ' ').replace('reset password token', 'Reset link');
              return `${label} ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`;
            });
          errorMessage = messages.join('. ');
        } else if (Array.isArray(errors)) {
          errorMessage = errors.join('. ');
        }
      } else if (error.data?.error) {
        errorMessage = error.data.error;
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  // Change password while authenticated — PATCH /password/change
  async changePassword(currentPassword, newPassword, newPasswordConfirmation) {
    try {
      await apiService.patch('/password/change', {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: newPasswordConfirmation,
      });
      return { success: true, message: 'Password changed successfully.' };
    } catch (error) {
      let errorMessage = 'Failed to change password. Please try again.';
      if (error.data?.errors) {
        const errors = error.data.errors;
        if (Array.isArray(errors)) {
          errorMessage = errors.join('. ');
        } else if (typeof errors === 'object') {
          errorMessage = Object.entries(errors)
            .map(([field, msgs]) => `${field.replace(/_/g, ' ')} ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join('. ');
        }
      } else if (error.data?.error) {
        errorMessage = error.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return { success: false, message: errorMessage };
    }
  }

  // Refresh token manually (for testing)
  async refreshToken() {
    try {
      const result = await apiService.refreshTokenIfNeeded();

      // Handle refresh response based on the exact API format
      if (result.status && result.status.code === 200 && result.status.data) {
        const { user } = result.status.data;

        // Update stored user data
        if (user) {
          localStorage.setItem('current_user', JSON.stringify(user));
        }

        return {
          success: true,
          user,
          message: result.status.message || 'Token refreshed successfully',
        };
      }

      return { success: true, data: result };
    } catch (error) {
      console.error('Token refresh error:', error);

      // If refresh fails, user needs to login again
      if (error.message?.includes('Invalid or expired token')) {
        apiService.removeToken();
        localStorage.removeItem('current_user');
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }

      return {
        success: false,
        message: error.message || 'Token refresh failed',
        error,
      };
    }
  }

  // Force refresh token (for development/testing)
  async forceRefreshToken() {
    try {
      console.log('Force refreshing token...');
      const result = await apiService.performTokenRefresh();
      console.log('Token refresh result:', result);
      return result;
    } catch (error) {
      console.error('Force token refresh failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();